import { apiClient } from './api';

export enum PayrollStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  TRANSFERRED = 'TRANSFERRED',
}

export enum SheetType {
  WAGE_SALARY = 'WAGE_SALARY',
  OTHER_INCOME = 'OTHER_INCOME',
}

export interface PayrollItem {
  id: number;
  payrollId: number;
  employeeId?: number;
  employeeName: string;
  joinDate?: string;
  endDate?: string;
  baseSalary: number;
  bonus: number;
  performanceBonus: number;
  qualificationAllowance: number;
  annualLeaveAllowance: number;
  mealAllowance: number;
  researchAllowance: number;
  childcareAllowance: number;
  vehicleAllowance: number;
  totalPayment: number;
  nationalPension: number;
  healthInsurance: number;
  employmentInsurance: number;
  longTermCare: number;
  incomeTax: number;
  localIncomeTax: number;
  totalDeduction: number;
  netPayment: number;
  sheetType: SheetType;
  memo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Payroll {
  id: number;
  year: number;
  month: number;
  paymentDate?: string;
  status: PayrollStatus;
  sourceFile?: string;
  totalPayment: number;
  totalDeduction: number;
  totalNetPayment: number;
  employeeCount: number;
  transferFileUrl?: string;
  socialInsuranceAmount: number; // 4대보험 총액
  socialInsuranceMemo?: string; // 적요 (예: "2606사회보험")
  socialInsuranceFetchedAt?: string;
  items: PayrollItem[];
  createdAt: string;
  updatedAt: string;
}

export const payrollApi = {
  // 급여대장 목록 조회
  getAll: async (): Promise<Payroll[]> => {
    return apiClient.getList<Payroll>('/payroll');
  },

  // 특정 급여대장 조회
  getById: async (id: number): Promise<Payroll> => {
    return apiClient.get<Payroll>(`/payroll/${id}`);
  },

  // 년/월로 급여대장 조회
  getByYearMonth: async (year: number, month: number): Promise<Payroll | null> => {
    try {
      return await apiClient.get<Payroll>(`/payroll/by-date/${year}/${month}`);
    } catch {
      return null;
    }
  },

  // 혜움에서 급여대장 다운로드 (Puppeteer 작업이라 오래 걸림 - 타임아웃 2분)
  downloadFromHeum: async (year: number, month: number): Promise<{ success: boolean; message: string; payroll: Payroll }> => {
    return apiClient.post('/payroll/download-from-heum', { year, month }, { timeoutMs: 120000 });
  },

  // 급여대장 상태 변경
  updateStatus: async (id: number, status: PayrollStatus): Promise<Payroll> => {
    return apiClient.put<Payroll>(`/payroll/${id}/status`, { status });
  },

  // 급여 항목 수정
  updateItem: async (itemId: number, data: Partial<PayrollItem>): Promise<PayrollItem> => {
    return apiClient.put<PayrollItem>(`/payroll/item/${itemId}`, data);
  },

  // 하나은행 이체파일 생성 (XLSX)
  generateHanaFile: async (id: number): Promise<{ success: boolean; message: string; filePath: string }> => {
    return apiClient.post(`/payroll/${id}/generate-hana`, {});
  },

  // 급여대장 삭제
  delete: async (id: number): Promise<void> => {
    return apiClient.delete(`/payroll/${id}`);
  },

  // 이체파일 이메일 전송
  sendTransferEmail: async (id: number, email?: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.post(`/payroll/${id}/send-transfer-email`, { email });
  },

  // 4대보험 조회 (혜움 입출금 크롤 - Puppeteer라 오래 걸림, 타임아웃 2분)
  fetchInsurance: async (id: number): Promise<{ success: boolean; message: string; socialInsuranceAmount: number; socialInsuranceMemo?: string }> => {
    return apiClient.post(`/payroll/${id}/fetch-insurance`, {}, { timeoutMs: 120000 });
  },

  // 4대보험 총액 수동 설정
  setInsurance: async (id: number, amount: number, memo?: string): Promise<{ success: boolean; message: string; socialInsuranceAmount: number }> => {
    return apiClient.put(`/payroll/${id}/insurance`, { amount, memo });
  },
};
